/**
 * usePartnerTradePreferences Hook
 *
 * Fetches the authenticated user's saved partner trade category IDs — the trades they
 * want partners for in their network.
 *
 * API Endpoint:
 * - GET /api/workflows/partner-network-progress/preferences
 *
 * The workflow endpoint scopes to the authenticated user server-side. Do NOT switch this
 * back to the generated `user_partner_trade_preferences` list endpoint: its `userProfileId`
 * query filter is snake_cased by the request-transform plugin before the generated handler
 * reads it, so the filter never applies and the response is a page of every user's rows.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface PartnerTradePreferencesResponse {
  success: boolean;
  data: { tradeCategoryIds: string[] };
}

/** Prefix key — invalidate this to refetch every cached user's preferences */
export const partnerTradePreferencesQueryKey = ['partner-trade-preferences'] as const;

/**
 * @param userProfileId - Scopes the cache entry so a different signed-in user can't read
 *                        the previous user's cached selections
 * @param enabled - Skip the request until it's needed (e.g. until the modal opens)
 * @returns The user's saved trade category IDs
 */
export function usePartnerTradePreferences(userProfileId?: string, enabled = true) {
  return useQuery({
    queryKey: [...partnerTradePreferencesQueryKey, userProfileId ?? null],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get<PartnerTradePreferencesResponse>(
        '/api/workflows/partner-network-progress/preferences'
      );
      return response.data?.tradeCategoryIds ?? [];
    },
    enabled,
    // The modal seeds its checkboxes from this data, so it must never render stale
    // selections after a save. Window focus is excluded so a refetch can't land in the
    // middle of an edit.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export default usePartnerTradePreferences;
